import { FC } from 'react';
import { X } from 'lucide-react';
import { type UserProject } from '@teachur/http-client';
import { formatCourseNames, formatFullName } from '../../utils/projectReviewFormatters';
import { useProjectReviewPermissions } from '../../hooks/useProjectReviewPermissions';
import { ProjectClaimActions } from './ProjectClaimActions';

interface ProjectDrawerHeaderProps {
  project: UserProject | null;
  onClose: () => void;
  onOpenSubmissionsHistory?: () => void;
}

export const ProjectDrawerHeader: FC<ProjectDrawerHeaderProps> = ({
  project,
  onClose,
  onOpenSubmissionsHistory,
}) => {
  const { currentUserId, canShowClaimButton, canShowDisclaimButton, canShowAdminUnclaimButton } =
    useProjectReviewPermissions(project);

  if (!project) return null;

  const studentName = formatFullName(
    project.user?.firstName,
    project.user?.lastName,
    'Unknown student'
  );

  const projectName = project.project?.name ?? 'Untitled project';
  const courseNames = formatCourseNames(project.project?.courses);

  return (
    <div className="bg-(--primary-O1000) px-4 py-4 text-(--neutral-white-NW1000)">
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <h4 className="m-0! [font-size:var(--fs-p2)]! font-semibold text-(--neutral-white-NW1000)!">
            {projectName}
          </h4>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-(--neutral-white-NW1000)"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <p className="m-0! [font-size:var(--fs-l4)]! font-semibold text-(--neutral-white-NW1000)!">
              {studentName}
            </p>

            <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-white-NW1000)!">
              {courseNames}
            </p>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2">
              <ProjectClaimActions
                project={project}
                currentUserId={currentUserId}
                showClaim={canShowClaimButton}
                showDisclaim={canShowDisclaimButton}
                showAdmin={canShowAdminUnclaimButton}
              />
            </div>

            <button
              type="button"
              onClick={onOpenSubmissionsHistory}
              className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-(--neutral-white-NW1000) [font-size:var(--fs-l5)]! font-medium underline"
              aria-label="View submission history"
            >
              History
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
